import type {Values} from '@wix/devzai-utils-common';
import {
    socialLinkGetUsernameFromHandle,
    stringTrimPrefix,
    validateIsEmail,
    validateIsMobilePhone,
    validateIsUrl
} from '@wix/devzai-utils-common';
import {
    bymoPagePropsCreateContentSettingMetadata,
    BymoPagePropsContentSettingType,
    BymoPagePropsContentSettingSemanticMeaning,
    type BymoPagePropsContentSettingsMetadata,
} from '../bymo-page-props-metadata/bymo-page-props-metadata';
import type {BymoPagePropsAiStringKey} from '../bymo-page-props-metadata/bymo-page-props-ai-strings';

export const DoppeDtoSocialLinkPlatform = {
    Facebook: 'Facebook', // https://facebook.com/facebookpageurl
    Instagram: 'Instagram', // @instagramusername
    Apple: 'Apple',
    Behance: 'Behance',
    Clubhouse: 'Clubhouse', // https://clubhouse.com/@profile
    Discord: 'Discord', // https://discord.com/invite/yourchannel
    Dribbble: 'Dribbble',
    Figma: 'Figma',
    Github: 'Github',
    Google: 'Google',
    LinkedIn: 'LinkedIn', // https://linkedin.com/in/username
    Medium: 'Medium',
    Pinterest: 'Pinterest', // https://pinterest.com/
    Reddit: 'Reddit', // https://reddit.com/
    Signal: 'Signal',
    Skype: 'Skype',
    Snapchat: 'Snapchat',
    AppleMusic: 'AppleMusic',
    Tidal: 'Tidal',
    Deezer: 'Deezer',
    Spotify: 'Spotify',
    Substack: 'Substack', // https://username.substack.com
    Telegram: 'Telegram', // https://t.me/username
    TikTok: 'TikTok', // @tiktokusername
    Tumblr: 'Tumblr',
    Twitch: 'Twitch',
    Twitter: 'Twitter',
    X: 'X',
    Threads: 'Threads',
    VK: 'VK',
    YouTube: 'YouTube',
    Email: 'Email',
    WhatsApp: 'WhatsApp',
    Website: 'Website',
    Phone: 'Phone',
    Zillow: 'Zillow',
} as const;

export type DoppeDtoSocialLinkPlatform = Values<typeof DoppeDtoSocialLinkPlatform>;

export interface DoppeDtoSocialLink {
    platform: DoppeDtoSocialLinkPlatform;
    url: string;
}

export function doppeDtoSocialLinkGetAllSupportedPlatforms(): DoppeDtoSocialLinkPlatform[] {
    return Object.values(DoppeDtoSocialLinkPlatform);
}

export function doppeDtoSocialLinkResolveUrl(doppeDtoSocialLink: DoppeDtoSocialLink) {
    switch (doppeDtoSocialLink.platform) {
        case DoppeDtoSocialLinkPlatform.Instagram: {
            const instagramUserName = socialLinkGetUsernameFromHandle(doppeDtoSocialLink.url);

            return instagramUserName !== undefined ? `https://www.instagram.com/${instagramUserName}/` : undefined;
        }
        case DoppeDtoSocialLinkPlatform.TikTok: {
            const tikTokUserName = socialLinkGetUsernameFromHandle(doppeDtoSocialLink.url);

            return tikTokUserName !== undefined ? `https://www.tiktok.com/@${tikTokUserName.toLowerCase()}/` : undefined;
        }
        case DoppeDtoSocialLinkPlatform.X:
        case DoppeDtoSocialLinkPlatform.Twitter: {
            const username = socialLinkGetUsernameFromHandle(doppeDtoSocialLink.url);

            return username !== undefined ? `https://twitter.com/${username}` : undefined;
        }
        case DoppeDtoSocialLinkPlatform.Threads: {
            const username = socialLinkGetUsernameFromHandle(doppeDtoSocialLink.url);

            return username !== undefined ? `https://www.threads.net/@${username}` : undefined;
        }
        case DoppeDtoSocialLinkPlatform.Twitch: {
            const username = socialLinkGetUsernameFromHandle(doppeDtoSocialLink.url);

            return username !== undefined ? `https://www.twitch.tv/${username}` : undefined;
        }
        case DoppeDtoSocialLinkPlatform.Email: {
            if (!validateIsEmail(doppeDtoSocialLink.url)) {
                return undefined;
            }

            return `mailto:${doppeDtoSocialLink.url}`;
        }
        case DoppeDtoSocialLinkPlatform.WhatsApp: {
            if (!validateIsMobilePhone(doppeDtoSocialLink.url)) {
                return undefined;
            }

            return `https://api.whatsapp.com/send?phone=${stringTrimPrefix(doppeDtoSocialLink.url, '+')}`;
        }
        case DoppeDtoSocialLinkPlatform.Phone: {
            // if (!validateIsMobilePhone(doppeDtoSocialLink.url)) {
            //     return undefined;
            // }

            return `tel:${doppeDtoSocialLink.url}`;
        }
    }

    if (
        validateIsUrl(doppeDtoSocialLink.url, {
            protocols: ['http', 'https'],
            require_protocol: true,
        })
    ) {
        return doppeDtoSocialLink.url;
    } else {
        return undefined;
    }
}

export function doppeDtoSocialLinkPlatformResolveUniqueName(platform: DoppeDtoSocialLinkPlatform) {
    switch (platform) {
        case DoppeDtoSocialLinkPlatform.Facebook:
            return 'Facebook';
        case DoppeDtoSocialLinkPlatform.Instagram:
            return 'Instagram';
        case DoppeDtoSocialLinkPlatform.Apple:
            return 'Apple';
        case DoppeDtoSocialLinkPlatform.Behance:
            return 'Behance';
        case DoppeDtoSocialLinkPlatform.Clubhouse:
            return 'Clubhouse';
        case DoppeDtoSocialLinkPlatform.Discord:
            return 'Discord';
        case DoppeDtoSocialLinkPlatform.Dribbble:
            return 'Dribbble';
        case DoppeDtoSocialLinkPlatform.Figma:
            return 'Figma';
        case DoppeDtoSocialLinkPlatform.Github:
            return 'Github';
        case DoppeDtoSocialLinkPlatform.Google:
            return 'Google';
        case DoppeDtoSocialLinkPlatform.LinkedIn:
            return 'LinkedIn';
        case DoppeDtoSocialLinkPlatform.Medium:
            return 'Medium';
        case DoppeDtoSocialLinkPlatform.Pinterest:
            return 'Pinterest';
        case DoppeDtoSocialLinkPlatform.Reddit:
            return 'Reddit';
        case DoppeDtoSocialLinkPlatform.Signal:
            return 'Signal';
        case DoppeDtoSocialLinkPlatform.Skype:
            return 'Skype';
        case DoppeDtoSocialLinkPlatform.Snapchat:
            return 'Snapchat';
        case DoppeDtoSocialLinkPlatform.Substack:
            return 'Substack';
        case DoppeDtoSocialLinkPlatform.AppleMusic:
            return 'Apple Music';
        case DoppeDtoSocialLinkPlatform.Tidal:
            return 'Tidal';
        case DoppeDtoSocialLinkPlatform.Deezer:
            return 'Deezer';
        case DoppeDtoSocialLinkPlatform.Spotify:
            return 'Spotify';
        case DoppeDtoSocialLinkPlatform.Telegram:
            return 'Telegram';
        case DoppeDtoSocialLinkPlatform.TikTok:
            return 'TikTok';
        case DoppeDtoSocialLinkPlatform.Tumblr:
            return 'Tumblr';
        case DoppeDtoSocialLinkPlatform.Twitch:
            return 'Twitch';
        case DoppeDtoSocialLinkPlatform.Twitter:
            return 'Twitter';
        case DoppeDtoSocialLinkPlatform.X:
            return 'X';
        case DoppeDtoSocialLinkPlatform.Zillow:
            return 'Zillow';
        case DoppeDtoSocialLinkPlatform.Threads:
            return 'Threads';
        case DoppeDtoSocialLinkPlatform.VK:
            return 'VK';
        case DoppeDtoSocialLinkPlatform.YouTube:
            return 'YouTube';
        case DoppeDtoSocialLinkPlatform.Email:
            return 'Email';
        case DoppeDtoSocialLinkPlatform.WhatsApp:
            return 'WhatsApp';
        case DoppeDtoSocialLinkPlatform.Website:
            return 'Website';
        case DoppeDtoSocialLinkPlatform.Phone:
            return 'Phone Number';
    }
}

type SocialLinkUrlMetadata = BymoPagePropsContentSettingsMetadata<Pick<DoppeDtoSocialLink, 'url'>>;

function doppeDtoSocialLinkCreateUrlMetadata(
    description: BymoPagePropsAiStringKey,
    aiValidator?: (value: unknown) => string | null,
): SocialLinkUrlMetadata {
    return {
        url: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Url, BymoPagePropsContentSettingSemanticMeaning.EntityPageUrl, {
            description: description,
            aiValidator: aiValidator,
        }),
    };
}

function doppeDtoSocialLinkAiValidatorSocialHandle(value: unknown): string | null {
    if (typeof value !== 'string') {
        return `Expected a social handle string, got ${typeof value}`;
    }
    if (!socialLinkGetUsernameFromHandle(value)) {
        return `Must be a social handle starting with @ (e.g., "@username")`;
    }
    return null;
}

function doppeDtoSocialLinkAiValidatorEmail(value: unknown): string | null {
    if (typeof value !== 'string') {
        return `Expected an email address string, got ${typeof value}`;
    }
    if (!validateIsEmail(value)) {
        return `Must be a valid email address (e.g., "hello@example.com")`;
    }
    return null;
}

function doppeDtoSocialLinkAiValidatorMobilePhone(value: unknown): string | null {
    if (typeof value !== 'string') {
        return `Expected a phone number string, got ${typeof value}`;
    }
    if (!validateIsMobilePhone(value)) {
        return `Must be a phone number with country code (e.g., "+15551234567")`;
    }
    return null;
}

function doppeDtoSocialLinkAiValidatorUrl(value: unknown): string | null {
    if (typeof value !== 'string') {
        return `Expected a URL string, got ${typeof value}`;
    }
    if (!validateIsUrl(value, {protocols: ['http', 'https'], require_protocol: true})) {
        return `Must be a full URL starting with https:// (e.g., "https://example.com/...")`;
    }
    return null;
}

export const DoppeDtoSocialLinkContentPropsMetadataByPlatform: Record<DoppeDtoSocialLinkPlatform, SocialLinkUrlMetadata> = {
    [DoppeDtoSocialLinkPlatform.Instagram]: doppeDtoSocialLinkCreateUrlMetadata('socialLink.Instagram.url', doppeDtoSocialLinkAiValidatorSocialHandle),
    [DoppeDtoSocialLinkPlatform.TikTok]: doppeDtoSocialLinkCreateUrlMetadata('socialLink.TikTok.url', doppeDtoSocialLinkAiValidatorSocialHandle),
    [DoppeDtoSocialLinkPlatform.X]: doppeDtoSocialLinkCreateUrlMetadata('socialLink.X.url', doppeDtoSocialLinkAiValidatorSocialHandle),
    [DoppeDtoSocialLinkPlatform.Twitter]: doppeDtoSocialLinkCreateUrlMetadata('socialLink.Twitter.url', doppeDtoSocialLinkAiValidatorSocialHandle),
    [DoppeDtoSocialLinkPlatform.Threads]: doppeDtoSocialLinkCreateUrlMetadata('socialLink.Threads.url', doppeDtoSocialLinkAiValidatorSocialHandle),
    [DoppeDtoSocialLinkPlatform.Twitch]: doppeDtoSocialLinkCreateUrlMetadata('socialLink.Twitch.url', doppeDtoSocialLinkAiValidatorSocialHandle),

    [DoppeDtoSocialLinkPlatform.Email]: doppeDtoSocialLinkCreateUrlMetadata('socialLink.Email.url', doppeDtoSocialLinkAiValidatorEmail),

    [DoppeDtoSocialLinkPlatform.WhatsApp]: doppeDtoSocialLinkCreateUrlMetadata('socialLink.WhatsApp.url', doppeDtoSocialLinkAiValidatorMobilePhone),
    [DoppeDtoSocialLinkPlatform.Phone]: doppeDtoSocialLinkCreateUrlMetadata('socialLink.Phone.url'),

    [DoppeDtoSocialLinkPlatform.Facebook]: doppeDtoSocialLinkCreateUrlMetadata('socialLink.Facebook.url', doppeDtoSocialLinkAiValidatorUrl),
    [DoppeDtoSocialLinkPlatform.Apple]: doppeDtoSocialLinkCreateUrlMetadata('socialLink.Apple.url', doppeDtoSocialLinkAiValidatorUrl),
    [DoppeDtoSocialLinkPlatform.Behance]: doppeDtoSocialLinkCreateUrlMetadata('socialLink.Behance.url', doppeDtoSocialLinkAiValidatorUrl),
    [DoppeDtoSocialLinkPlatform.Clubhouse]: doppeDtoSocialLinkCreateUrlMetadata('socialLink.Clubhouse.url', doppeDtoSocialLinkAiValidatorUrl),
    [DoppeDtoSocialLinkPlatform.Discord]: doppeDtoSocialLinkCreateUrlMetadata('socialLink.Discord.url', doppeDtoSocialLinkAiValidatorUrl),
    [DoppeDtoSocialLinkPlatform.Dribbble]: doppeDtoSocialLinkCreateUrlMetadata('socialLink.Dribbble.url', doppeDtoSocialLinkAiValidatorUrl),
    [DoppeDtoSocialLinkPlatform.Figma]: doppeDtoSocialLinkCreateUrlMetadata('socialLink.Figma.url', doppeDtoSocialLinkAiValidatorUrl),
    [DoppeDtoSocialLinkPlatform.Github]: doppeDtoSocialLinkCreateUrlMetadata('socialLink.Github.url', doppeDtoSocialLinkAiValidatorUrl),
    [DoppeDtoSocialLinkPlatform.Google]: doppeDtoSocialLinkCreateUrlMetadata('socialLink.Google.url', doppeDtoSocialLinkAiValidatorUrl),
    [DoppeDtoSocialLinkPlatform.LinkedIn]: doppeDtoSocialLinkCreateUrlMetadata('socialLink.LinkedIn.url', doppeDtoSocialLinkAiValidatorUrl),
    [DoppeDtoSocialLinkPlatform.Medium]: doppeDtoSocialLinkCreateUrlMetadata('socialLink.Medium.url', doppeDtoSocialLinkAiValidatorUrl),
    [DoppeDtoSocialLinkPlatform.Pinterest]: doppeDtoSocialLinkCreateUrlMetadata('socialLink.Pinterest.url', doppeDtoSocialLinkAiValidatorUrl),
    [DoppeDtoSocialLinkPlatform.Reddit]: doppeDtoSocialLinkCreateUrlMetadata('socialLink.Reddit.url', doppeDtoSocialLinkAiValidatorUrl),
    [DoppeDtoSocialLinkPlatform.Signal]: doppeDtoSocialLinkCreateUrlMetadata('socialLink.Signal.url', doppeDtoSocialLinkAiValidatorUrl),
    [DoppeDtoSocialLinkPlatform.Skype]: doppeDtoSocialLinkCreateUrlMetadata('socialLink.Skype.url', doppeDtoSocialLinkAiValidatorUrl),
    [DoppeDtoSocialLinkPlatform.Snapchat]: doppeDtoSocialLinkCreateUrlMetadata('socialLink.Snapchat.url', doppeDtoSocialLinkAiValidatorUrl),
    [DoppeDtoSocialLinkPlatform.AppleMusic]: doppeDtoSocialLinkCreateUrlMetadata('socialLink.AppleMusic.url', doppeDtoSocialLinkAiValidatorUrl),
    [DoppeDtoSocialLinkPlatform.Tidal]: doppeDtoSocialLinkCreateUrlMetadata('socialLink.Tidal.url', doppeDtoSocialLinkAiValidatorUrl),
    [DoppeDtoSocialLinkPlatform.Deezer]: doppeDtoSocialLinkCreateUrlMetadata('socialLink.Deezer.url', doppeDtoSocialLinkAiValidatorUrl),
    [DoppeDtoSocialLinkPlatform.Spotify]: doppeDtoSocialLinkCreateUrlMetadata('socialLink.Spotify.url', doppeDtoSocialLinkAiValidatorUrl),
    [DoppeDtoSocialLinkPlatform.Substack]: doppeDtoSocialLinkCreateUrlMetadata('socialLink.Substack.url', doppeDtoSocialLinkAiValidatorUrl),
    [DoppeDtoSocialLinkPlatform.Telegram]: doppeDtoSocialLinkCreateUrlMetadata('socialLink.Telegram.url', doppeDtoSocialLinkAiValidatorUrl),
    [DoppeDtoSocialLinkPlatform.Tumblr]: doppeDtoSocialLinkCreateUrlMetadata('socialLink.Tumblr.url', doppeDtoSocialLinkAiValidatorUrl),
    [DoppeDtoSocialLinkPlatform.VK]: doppeDtoSocialLinkCreateUrlMetadata('socialLink.VK.url', doppeDtoSocialLinkAiValidatorUrl),
    [DoppeDtoSocialLinkPlatform.YouTube]: doppeDtoSocialLinkCreateUrlMetadata('socialLink.YouTube.url', doppeDtoSocialLinkAiValidatorUrl),
    [DoppeDtoSocialLinkPlatform.Website]: doppeDtoSocialLinkCreateUrlMetadata('socialLink.Website.url', doppeDtoSocialLinkAiValidatorUrl),
    [DoppeDtoSocialLinkPlatform.Zillow]: doppeDtoSocialLinkCreateUrlMetadata('socialLink.Zillow.url', doppeDtoSocialLinkAiValidatorUrl),
};
